import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, ExternalLink, MessageCircle } from 'lucide-react';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSTable } from '../../components/ILMSTable';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSInput } from '../../components/ILMSForm';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatDateTime } from '../../utils/format';
import { agentApi, type AgentOverview } from '../../agent/agentApi';

// The office's live view of the delivery agents (refreshes every 30 s), and
// the link to send them: /agent works in any phone browser with their staff
// login - no app needed. Same data the mobile app's Agents screen shows.

const MODE: Record<string, string> = { QRScan: 'QR', OfflineBypass: 'No QR (GPS)', Skipped: 'Skipped' };

function AgentDetailModal({ agent, onClose }: { agent: AgentOverview; onClose: () => void }) {
  const q = useQuery({ queryKey: ['agents-live', agent.user_id], queryFn: () => agentApi.detail(agent.user_id), refetchInterval: 30_000 });
  const d = q.data;
  return (
    <ILMSModal open onClose={onClose} title={`${agent.username} — today`} size="lg">
      {q.isError ? <ILMSAlert tone="critical">{getApiErrorMessage(q.error)}</ILMSAlert> : !d ? <p className="text-secondary text-ink-500">Loading…</p> : (
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-label mb-1">Still to deliver ({d.pending.length})</p>
            {d.pending.length ? (
              <ul className="flex flex-col gap-1 text-secondary">
                {d.pending.map((p) => (
                  <li key={`${p.customer_id}-${p.sale_date}-${p.shift}`} className="flex justify-between gap-2">
                    <span>{p.customer_name} · {p.shift === 'Morning' ? 'AM' : 'PM'} · {p.sale_date}{p.is_current_shift ? ' · due now' : ''}</span>
                    <span className="tabular-nums">{Number(p.litres)} L</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-secondary text-ink-500">Nothing pending.</p>}
          </div>
          <div>
            <p className="text-label mb-1">Done today ({d.done_today.length})</p>
            {d.done_today.length ? (
              <ul className="flex flex-col gap-1 text-secondary">
                {d.done_today.map((x) => (
                  <li key={x.id} className="flex justify-between gap-2">
                    <span>{x.customer_name} · {x.shift === 'Morning' ? 'AM' : 'PM'} · {MODE[x.verification_mode] ?? x.verification_mode} · {formatDateTime(x.captured_at)}</span>
                    <span className="tabular-nums">{x.litres != null ? `${Number(x.litres)} L` : '—'}{x.sale_status === 'Pending' ? ' · to approve' : ''}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-secondary text-ink-500">None yet.</p>}
          </div>
          <div>
            <p className="text-label mb-1">Tasks</p>
            {d.tasks.length ? (
              <ul className="flex flex-col gap-1 text-secondary">
                {d.tasks.map((t) => (
                  <li key={t.assignment_id} className="flex justify-between gap-2">
                    <span>{t.title}</span>
                    <span>{t.completed_at ? 'Done' : t.started_at ? 'Started' : 'Open'}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-secondary text-ink-500">No tasks.</p>}
          </div>
        </div>
      )}
    </ILMSModal>
  );
}

export function AgentsTab() {
  const { can } = useAuth();
  const [open, setOpen] = useState<AgentOverview | null>(null);
  const [copied, setCopied] = useState(false);
  const [mobile, setMobile] = useState('');
  const allowed = can('Deliveries', 'Approve');
  const list = useQuery({ queryKey: ['agents-live'], queryFn: agentApi.overview, enabled: allowed, refetchInterval: 30_000 });
  const link = `${window.location.origin}/agent`;
  const message = `Sri KalpaGavya delivery page: ${link}\nOpen it in your phone's browser and sign in with your username and password. Then use "Add to Home screen" to keep it like an app.`;
  const digits = mobile.replace(/\D/g, '').slice(-10);

  return (
    <div className="flex flex-col gap-4">
      <ILMSCard>
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-card-title">Agent page — no app needed</p>
            <p className="text-secondary text-ink-600">
              Send this link to an agent. They open it in the phone's browser and sign in with their normal username and password (Users under Administration).
              They can scan the customer's QR, record a delivery without QR (GPS), skip, change a day, record milking (one cow or a batch, cows picked or scanned by QR / barcode),
              record milk delivered to the Dairy Hub (slip photo), a Partner, Customer or Vendor (scan their QR or record without QR) or used for calves / office,
              do their tasks and report spillage.
              Everything they save shows here straight away.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-md bg-surface-subtle px-3 py-2 text-secondary">{link}</code>
            <ILMSButton size="sm" variant="outline" icon={<Copy />} onClick={() => { navigator.clipboard?.writeText(link); setCopied(true); }}>{copied ? 'Copied' : 'Copy link'}</ILMSButton>
            <a href="/agent" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-secondary text-info-600 hover:underline">Open it <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ILMSInput className="max-w-[14rem]" inputMode="numeric" placeholder="Agent's mobile number" value={mobile} onChange={(e) => setMobile(e.target.value)} />
            <a href={digits.length === 10 ? `https://wa.me/91${digits}?text=${encodeURIComponent(message)}` : `https://wa.me/?text=${encodeURIComponent(message)}`}
              target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-md bg-success-600 px-3 py-2 text-secondary font-semibold text-white">
              <MessageCircle className="h-4 w-4" /> Send on WhatsApp
            </a>
          </div>
        </div>
      </ILMSCard>

      {!allowed ? (
        <ILMSAlert tone="info">The live list of agents needs Deliveries → Approve access.</ILMSAlert>
      ) : (
        <ILMSCard padded={false}>
          <div className="flex items-center justify-between gap-2 border-b border-ink-300/10 p-3">
            <p className="text-card-title">Agents today</p>
            <span className="text-helper-text text-ink-400">Live · updates every 30 seconds{list.dataUpdatedAt ? ` · ${new Date(list.dataUpdatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''}</span>
          </div>
          <div className="px-3 pt-1 pb-3">
            <ILMSTable
              columns={[
                { key: 'name', header: 'Agent', primary: true, render: (a) => a.username },
                { key: 'customers', header: 'Customers', align: 'right', render: (a) => a.assigned_customers },
                { key: 'now', header: 'To deliver now', align: 'right', render: (a) => (a.deliveries_pending_now
                  ? <ILMSStatusBadge status={String(a.deliveries_pending_now)} tone="warning" size="sm" /> : '0'), sortValue: (a) => a.deliveries_pending_now },
                { key: 'earlier', header: 'Missed earlier', align: 'right', render: (a) => (a.deliveries_pending_earlier
                  ? <ILMSStatusBadge status={String(a.deliveries_pending_earlier)} tone="critical" size="sm" /> : '0'), sortValue: (a) => a.deliveries_pending_earlier },
                { key: 'done', header: 'Done today', align: 'right', render: (a) => a.deliveries_done_today },
                { key: 'skipped', header: 'Skipped', align: 'right', hideOnTablet: true, render: (a) => a.deliveries_skipped_today },
                { key: 'tasks', header: 'Tasks open', align: 'right', hideOnTablet: true, render: (a) => `${a.tasks_open}${a.tasks_overdue ? ` (${a.tasks_overdue} late)` : ''}` },
                { key: 'last', header: 'Last activity', render: (a) => (a.last_activity_at ? formatDateTime(a.last_activity_at) : '—'), sortValue: (a) => a.last_activity_at ?? '' },
              ]}
              data={list.data ?? []}
              rowKey={(a) => a.user_id}
              onRowClick={setOpen}
              exportTitle="Agents today"
              isLoading={list.isLoading}
              error={list.isError ? getApiErrorMessage(list.error) : null}
              emptyTitle="No agents yet."
              emptyDescription="Give a user the Operator role, and pick them as the Delivery Agent on their customers."
            />
          </div>
        </ILMSCard>
      )}
      {open && <AgentDetailModal agent={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
